import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmailRequestType,
  LabelId,
  UserCommunication,
} from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';
import { pushGTMEvent } from '../../../utils/analytics/gtm';
import { useCustomToast } from '../../useCustomToast';

export const EMAIL_MARKER = { STAR: 'STAR', IMPORTANT: 'IMPORTANT' } as const;
export type EmailMarker = keyof typeof EMAIL_MARKER;

const IMPORTANT_TOAST_KEY = {
  on: 'inbox.userCommunication.actions.markImportantSuccess',
  off: 'inbox.userCommunication.actions.unmarkImportantSuccess',
} as const;

const MARKER = {
  STAR: {
    label: LabelId.STARRED,
    on: EmailRequestType.STAR,
    off: EmailRequestType.UNSTAR,
  },
  IMPORTANT: {
    label: LabelId.IMPORTANT,
    on: EmailRequestType.MARK_IMPORTANT,
    off: EmailRequestType.UNMARK_IMPORTANT,
  },
} as const;

export const useToggleEmailMarker = () => {
  const { t } = useTranslation();
  const { errorMessage, successMessage } = useCustomToast();

  const toggleMarker = useCallback(
    async (uc: UserCommunication, marker: EmailMarker, source = '') => {
      if (!uc.userEmailAddress || !uc.platform) {
        return;
      }
      const { updateUserCommunicationLabels } =
        useUserCommunicationsStore.getState();
      const { label, on, off } = MARKER[marker];
      const prev = uc.labelIds || [];
      const isOn = prev.includes(label);
      const action = isOn ? off : on;
      const next = isOn ? prev.filter((id) => id !== label) : [...prev, label];

      if (marker === EMAIL_MARKER.STAR) {
        pushGTMEvent({
          event: 'inbox_star_toggled',
          action: isOn ? 'unstar' : 'star',
          message_id: uc.messageId,
          platform: uc.platform,
          source,
        });
      } else {
        pushGTMEvent({
          event: 'inbox_important_toggled',
          action: isOn ? 'unmark' : 'mark',
          message_id: uc.messageId,
          platform: uc.platform,
          source,
        });
      }

      updateUserCommunicationLabels(uc.messageId, next);
      try {
        await modifyEmail({
          userEmailAddress: uc.userEmailAddress,
          platform: uc.platform,
          messageId: uc.messageId,
          action,
        });
        if (marker === EMAIL_MARKER.IMPORTANT) {
          successMessage(
            t(isOn ? IMPORTANT_TOAST_KEY.off : IMPORTANT_TOAST_KEY.on),
          );
        }
      } catch (error) {
        log.error('Failed to toggle email marker', {
          messageId: uc.messageId,
          marker,
          error,
        });
        updateUserCommunicationLabels(uc.messageId, prev);
        errorMessage(t('inbox.userCommunication.actions.markerError'));
      }
    },
    [],
  );

  return { toggleMarker };
};
